import React from 'react';

interface TaskProgressBarProps {
  estimate: number;
  spent: number;
  /** Remaining hours; if null/undefined, it's derived as estimate - spent */
  remaining?: number | null;
}

// Clamp a percentage value into the safe 0–100 range, treating NaN as 0
const clampPercent = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
};

/**
 * Progress bar showing spent vs estimated time on a task card.
 *
 * Bar math:
 * - Not over budget: blue segment width = spent / estimate (relative to estimate),
 *   the rest of the track stays empty (grey) up to 100%.
 * - Over budget: the whole track represents `spent` hours. Blue segment width =
 *   estimate / spent (the portion that was "within budget"), red segment =
 *   the remaining width (the overage), so blue + red always sum to 100%.
 */
const TaskProgressBar: React.FC<TaskProgressBarProps> = ({ estimate, spent, remaining }) => {
  const safeEstimate = Math.max(0, estimate ?? 0);
  const safeSpent = Math.max(0, spent ?? 0);
  const computedRemaining = remaining ?? safeEstimate - safeSpent;
  const isOver = computedRemaining < 0;
  const overHours = Math.abs(computedRemaining);

  let bluePercent = 0;
  let redPercent = 0;

  if (isOver && safeSpent > 0) {
    bluePercent = (safeEstimate / safeSpent) * 100;
    redPercent = 100 - bluePercent;
  } else if (safeEstimate > 0) {
    bluePercent = (safeSpent / safeEstimate) * 100;
  }

  bluePercent = clampPercent(bluePercent);
  redPercent = clampPercent(redPercent);

  return (
    <div className="kanban-card__progress">
      <div className="kanban-card__progress-scale">
        <span>0</span>
        <span>Estimate: {safeEstimate}h</span>
      </div>
      <div className="kanban-card__progress-track">
        <div className="kanban-card__progress-fill" style={{ width: `${bluePercent}%` }} />
        {isOver && (
          <div
            className="kanban-card__progress-fill kanban-card__progress-fill_over"
            style={{ width: `${redPercent}%` }}
          />
        )}
      </div>
      <div className="kanban-card__progress-labels">
        <span className="kanban-card__progress-label">
          <i className="kanban-card__progress-dot kanban-card__progress-dot_spent" />
          Spent {safeSpent}h
        </span>
        {isOver ? (
          <span className="kanban-card__progress-label kanban-card__progress-label_over">
            <i className="kanban-card__progress-dot kanban-card__progress-dot_over" />
            {overHours}h over
          </span>
        ) : (
          <span className="kanban-card__progress-label">
            <i className="kanban-card__progress-dot kanban-card__progress-dot_remaining" />
            Remaining {computedRemaining}h
          </span>
        )}
      </div>
    </div>
  );
};

export default TaskProgressBar;
